import { isEmpty } from 'lodash';
import { IDeliveryDropOffCoordinatesHttpDto } from './interfaces/IDeliveryDropOffCoordinatesHttpDto';
import combineTokenisedAddress from '../utils/ordering/combineTokenisedAddress';

export interface IDeliveryDropOffCoordinatesContentDto {
  readonly deliveryDropOffCoordinates?: IDeliveryDropOffCoordinatesHttpDto;
}

export class DeliveryAddressDto {
  public static toDeliveryDropOffCoordinatesContentDto(
    deliveryAddress: DeliveryAddress | null,
  ): IDeliveryDropOffCoordinatesContentDto {
    if (!deliveryAddress) return {};
    const deliveryDropOffCoordinates =
      deliveryAddress.deliveryDropOffCoordinates;
    const location = deliveryAddress?.location;
    const hasLocation = !isEmpty(location);

    const fallbackReturnValue = hasLocation
      ? { deliveryDropOffCoordinates: location }
      : {};

    const hasDeliveryDropOffCoordinates = !isEmpty(deliveryDropOffCoordinates);
    const returnValue = hasDeliveryDropOffCoordinates
      ? { deliveryDropOffCoordinates }
      : {};

    return !isEmpty(returnValue) ? returnValue : fallbackReturnValue;
  }

  /**
   * Returns a short address string (unit + street number).
   * Suitable for API calls and UI titles.
   * e.g. "2/123 Main St"
   */
  public static toShortAddressString(
    deliveryAddress: DeliveryAddress | null,
  ): string {
    return combineTokenisedAddress({
      unit: deliveryAddress?.buildingInformation,
      streetNumber: deliveryAddress?.displayAddress?.firstLine,
    });
  }

  /**
   * Returns a locality-only string (suburb + postcode + state).
   * Suitable for UI subtitles where the title is the short address.
   * e.g. "Suburb, 1234, NSW"
   */
  public static toLocalityString(
    deliveryAddress: DeliveryAddress | null,
  ): string {
    return combineTokenisedAddress({
      suburb: deliveryAddress?.addressComponents?.suburb,
      postcode: deliveryAddress?.addressComponents?.postCode,
      state: deliveryAddress?.addressComponents?.state,
    });
  }

  /**
   * Returns the full address string (unit + street + suburb + postcode + state).
   * Suitable for full-line display and summaries.
   * e.g. "2/123 Main St, Suburb, 1234, NSW"
   */
  public static toFullAddressString(
    deliveryAddress: DeliveryAddress | null,
  ): string {
    return combineTokenisedAddress({
      unit: deliveryAddress?.buildingInformation,
      streetNumber: deliveryAddress?.displayAddress?.firstLine,
      suburb: deliveryAddress?.addressComponents?.suburb,
      postcode: deliveryAddress?.addressComponents?.postCode,
      state: deliveryAddress?.addressComponents?.state,
    });
  }
}
